import React, { FC } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, ReferenceLine } from 'recharts'
import { FinancialBarChartData } from 'utils/types'
import { useTheme, Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useCurrencyConversion } from 'hooks/useCurrencyConversion'
import { getAbbreviatedNumberString } from 'components/Income/Income'
import { useCurrency } from 'contexts'

const fontFamily = 'Source Sans Pro'

type BarDataKey = 'reported' | 'exact' | 'documented' | 'extrapolated' | 'estimated' | string

interface GenericBarsChartProps {
  chartData: FinancialBarChartData[]
  xAxisDataKey: string
  barDataKey1: BarDataKey
  barDataKey2: BarDataKey
  stackedBarDataKey1: BarDataKey
  barsRadius?: [number, number, number, number]
  labels?: Record<BarDataKey, string>
}

interface CustomTheme extends Theme {
  palette: Theme['palette'] & {
    chartsTheme: Record<string, string>
  }
}

const GenericBarsChart: FC<GenericBarsChartProps> = ({
  chartData,
  barDataKey1,
  barDataKey2,
  xAxisDataKey,
  barsRadius = [6, 6, 0, 0],
  stackedBarDataKey1,
  labels = {},
}) => {
  const theme = useTheme() as CustomTheme
  const isXxxl = useMediaQuery(theme.breakpoints.up('xxxl'))
  const { currentCurrency } = useCurrencyConversion()
  const { currencies } = useCurrency()

  const getTickFontSize = (): number => (isXxxl ? 38 : 14)
  const getBarSize = (): number => (isXxxl ? 26 : 16)

  const tickSize = getTickFontSize()
  const barSize = getBarSize()

  const defaultLabels: Record<BarDataKey, string> = {
    reported: 'Reported',
    exact: 'Exact',
    documented: 'Documented',
    extrapolated: 'Extrapolated',
    estimated: 'Estimated',
  }

  const barDataKeyToLabel: Record<BarDataKey, string> = {
    ...defaultLabels,
    ...labels,
  }

  const getCurrencySymbol = (currencyCode: string): string => {
    const currency = currencies.find((c: any) => c.code === currencyCode)
    return currency?.symbol || currencyCode
  }

  const formatTooltipValue = (value: any): string => {
    if (typeof value === 'number') {
      const abbreviatedValue = getAbbreviatedNumberString(value)
      const currencySymbol = getCurrencySymbol(currentCurrency)

      return `${currencySymbol}${abbreviatedValue}`
    }
    return String(value)
  }

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <BarChart data={chartData} barGap={4} barCategoryGap={0}>
        <CartesianGrid vertical={false} stroke='#87cefa' strokeWidth={0.2} strokeDasharray='1' />
        <XAxis
          dataKey={xAxisDataKey}
          tickLine={false}
          tick={{ fontFamily, fontSize: tickSize }}
          axisLine={{ stroke: 'transparent' }}
        />
        <ReferenceLine y={0} stroke='#b0b3b6' strokeWidth={2} />

        <Tooltip
          cursor={false}
          filterNull={true}
          contentStyle={{ fontFamily, textTransform: 'capitalize' }}
          formatter={formatTooltipValue}
        />
        <Bar
          dataKey={barDataKey1}
          fill={theme.palette.chartsTheme[barDataKey1]}
          radius={barsRadius}
          barSize={barSize}
          fontFamily={fontFamily}
          name={barDataKeyToLabel[barDataKey1] || barDataKey1}
        />
        <Bar
          dataKey={barDataKey2}
          fill={theme.palette.chartsTheme[barDataKey2]}
          barSize={barSize}
          fontFamily={fontFamily}
          stackId='a'
          name={barDataKeyToLabel[barDataKey2] || barDataKey2}
        />
        <Bar
          dataKey={stackedBarDataKey1}
          fill={theme.palette.chartsTheme[stackedBarDataKey1]}
          barSize={barSize}
          radius={barsRadius}
          fontFamily={fontFamily}
          stackId='a'
          name={barDataKeyToLabel[stackedBarDataKey1] || stackedBarDataKey1}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

export default GenericBarsChart
